import React, { ChangeEvent } from "react";
import { Box, Button, Typography, TextField, CircularProgress } from "@mui/material";
import { useAppContext } from "../../../../context/AppContext";
import { COMMON_MUI_STYLES } from "../../../../styles";
import ApiService from "../../../../services/api";

interface ForgotPasswordFormProps {
  email: string;
  loginErrors: { [key: string]: string };
  isLoading: boolean;
  sessionId: string | null;
  onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onValidateEmail: (email: string) => string | null;
  onSetLoginErrors: (errors: { [key: string]: string }) => void;
  onSetIsLoading: (loading: boolean) => void;
  onSetSnackbar: (snackbar: { message: string; open: boolean; severity: "success" | "error" | "warning" | "info" }) => void;
  onSetFormState: (updater: (prev: any) => any) => void;
  onSetCurrentView: (view: any) => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  email,
  loginErrors,
  isLoading,
  sessionId,
  onEmailChange,
  onValidateEmail,
  onSetLoginErrors,
  onSetIsLoading,
  onSetSnackbar,
  onSetFormState,
  onSetCurrentView,
}) => {
  const { getTranslation } = useAppContext();

  const handleSubmit = async () => {
    try {
      // Validate email before proceeding
      const emailError = onValidateEmail(email);
      if (emailError) {
        onSetLoginErrors({ email: emailError });
        return;
      }

      // Clear any existing errors
      onSetLoginErrors({});
      onSetIsLoading(true);
      
      const api = new ApiService(sessionId);
      await api.sendOtpForgotPassword(email.trim().toLowerCase());

      onSetSnackbar({
        message: getTranslation("Txt910"),
        open: true,
        severity: "success",
      });

      // Open email verification dialog
      onSetFormState((prev: any) => ({
        ...prev,
        showEmailVerificationModal: true,
        resendCountdown: 120, // Start 2-minute countdown
      }));
    } catch (error: any) {
      onSetSnackbar({
        message: getTranslation("Err82"),
        open: true,
        severity: "error",
      });
    } finally {
      onSetIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minWidth: { xs: "90vw", sm: "35%" },
        width: { xs: "90vw", sm: "35%" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      <Typography sx={COMMON_MUI_STYLES.title1}>
        {getTranslation("Txt14")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "var(--color-text-primary)", fontWeight: 500, textAlign: "center" }}
      >
        {getTranslation("Txt15")}
      </Typography>
      <TextField
        margin="normal"
        fullWidth
        id="email"
        placeholder={getTranslation("Txt16")}
        name="email"
        autoComplete="email"
        value={email}
        onChange={onEmailChange}
        error={!!loginErrors.email}
        helperText={loginErrors.email}
        sx={COMMON_MUI_STYLES.textField}
      />
      <Button
        variant="contained"
        fullWidth
        disabled={isLoading}
        sx={{
          backgroundColor: "var(--color-primary)",
          color: "var(--color-text-inverse)",
          textTransform: "none",
          fontSize: "1rem",
          fontWeight: 600,
          py: 1.5,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: isLoading ? "var(--color-primary)" : "var(--color-primary-hover)",
          },
        }}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} sx={{ color: "var(--color-text-inverse)" }} />
          </Box>
        ) : (
          getTranslation("Txt17")
        )}
      </Button>
      <Button
        variant="text"
        onClick={() => onSetCurrentView("contact")}
        sx={{
          mt: 1,
          textTransform: "none",
          color: "var(--color-text-primary)",
          fontWeight: 500,
          fontSize: "0.95rem",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: "1.2rem", textDecoration: "none" }}
        >
          {"<"}
        </Box>
        <Box component="span" sx={{ textDecoration: "underline" }}>
          {getTranslation("Txt18")}
        </Box>
      </Button>
    </Box>
  );
};
